import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import selectors from 'store/selectors';
import CustomDialog from 'components/CustomDialog/customDialog';
import { setDialog } from 'store/actions/dialogActions';
import {
  ContentCnt,
  CoverImage,
  CoverImageContainer,
  Requirments,
  pinnedContainer,
  pinnedText,
} from './Acknowledge.style';
import DIALOGS from 'utils/dialogIds';
import { Box, CircularProgress, Typography } from '@mui/material';
import xeniaApi from 'api/index';
import ImageUploaderSection from './ImageUploaderSection';
import VideoUploaderSection from './VideoUploaderSection';
import SignatureSection from './SignatureSection';
import { showMessageNotification } from 'utils/globalFunction';
import AnnouncementAttachments from 'pages/announcements-page/Builder/Attachments/attachments';
import CustomButton from 'components/Button/CustomButton';
import CheckIcon from '@mui/icons-material/CheckCircle';
import { AnnouncementFormData } from 'pages/announcements-page/Builder/interfaces/AnnouncementFormData';
import actions from 'store/actions';
import { LoaderCnt } from 'components/AttachChecklistModal/styled';
import { S3_BUCKET_ASSETS } from 'utils/constants';
const AcknowledgeAnnouncementDialog = () => {
  const dispatch = useDispatch();
  const dialogState = useSelector(selectors.getDialogState);
  const { open, data = {} } =
    dialogState?.[DIALOGS?.ACKNOWLEDGE_ANNOUNCEMENT_DIALOG] || {};
  const { announcementLog, tab, readOnly = false } = data;
  const announcement: AnnouncementFormData = announcementLog?.Announcement;

  const [photos, setPhotos] = useState<{ fileUrl: string; url: string }[]>(
    announcementLog?.photos.map((photo) => ({
      url: photo,
    })) ?? [],
  );
  const [video, setVideo] = useState<{ fileUrl: string; url: string } | null>(
    announcementLog?.video ?? null,
  );
  const [signature, setSignature] = useState<{ value: string } | null>(
    announcementLog?.signature ? { value: announcementLog?.signature } : null,
  );
  const [isClosing, setIsClosing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!announcementLog || !announcement) return null;

  const handleClose = async () => {
    setIsClosing(true);
    if (readOnly) {
      dispatch(
        setDialog({
          open: false,
          dialogId: DIALOGS?.ACKNOWLEDGE_ANNOUNCEMENT_DIALOG,
        }),
      );
      return;
    }
    const postObj = {
      status: tab,
    };
    const announcementsApiResponse = await xeniaApi.getUserAnnouncements(
      postObj,
    );
    if (announcementsApiResponse) {
      dispatch(actions.setAnnouncementLogs(announcementsApiResponse.logs));
    }
    dispatch(
      setDialog({
        open: false,
        dialogId: DIALOGS?.ACKNOWLEDGE_ANNOUNCEMENT_DIALOG,
      }),
    );
  };

  useEffect(() => {
    handleAcknowledge(false);
  }, [photos, video]);

  const handleAcknowledge = async (finish = false) => {
    if (readOnly) return;
    try {
      setLoading(true);
      setError(null);

      // Create submission payload
      const payload: any = {
        announcementId: announcement.id,
      };

      // Add media if required
      if (announcement.requirePhoto) {
        payload.photos = photos.map((photo) => photo.fileUrl || photo.url);
      }

      if (announcement.requireVideo) {
        payload.video = video?.fileUrl || video?.url || video;
      }

      if (announcement.requireSignature) {
        payload.signature = signature?.value;
      }

      // // Call API to acknowledge announcement
      if (announcement.id) {
        await xeniaApi.acknowledgeAnnouncement(
          payload,
          announcement.id,
          finish,
        );
      } else {
        setError('Announcement ID is missing');
        setLoading(false);
        return;
      }

      // Show success message or toast notification
      if (finish) {
        showMessageNotification('Acknowledgment Sent!');
        handleClose();
      }
    } catch (err) {
      console.error('Error acknowledging announcement:', err);
      setError('Failed to submit acknowledgment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Check if user has met all requirements
  const canAcknowledge = () => {
    if (readOnly) return false;
    if (!announcementLog) return false;

    if (announcement.status !== 'active') return false;

    if (announcement.requirePhoto) {
      // Check if photos requirement is met
      if (photos.length === 0) return false;

      // If multiple photos are required, check if the minimum count is met
      if (
        announcement.allowMultiplePhotos &&
        photos.length < (announcement.photoLimit || 1)
      ) {
        return false;
      }
    }

    // Check video requirement
    if (announcement.requireVideo && !video) return false;

    // Check signature requirement
    if (announcement.requireSignature && !signature) return false;

    return true;
  };
  const handleViewFullscreen = () => {
    dispatch(
      actions.setDialog({
        dialogId: DIALOGS.PHOTOS_ATTACHMENTS_PREVIEW,
        open: true,
        data: {
          allAttachments: [
            { url: announcement?.coverImage, name: 'Cover Photo' },
          ],
          id: announcement?.coverImage,
        },
      }),
    );
  };

  return (
    <>
      <CustomDialog
        hideActions={true}
        dialogProps={{
          open: open,
          maxWidth: true,
          onClose: handleClose,
          sx: {
            '& .MuiDialog-paper': {
              maxWidth: '700px',
            },
            '& .MuiDialogContent-root': {
              maxWidth: '100%',
              width: 700,
            },
          },
        }}
        header={
          <Box
            sx={{
              position: 'fixed',
              zIndex: 1310, // Higher than MuiDialog's default zIndex (1300)
              right: 'calc(47% - 365px)', // Position it next to the dialog's right edge
              backgroundColor: 'transparent',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
            onClick={handleClose}
          >
            <svg
              width="36"
              height="36"
              viewBox="0 0 36 36"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="0.5"
                y="0.5"
                width="35"
                height="35"
                rx="17.5"
                stroke="#9E9E9E"
              />
              <path
                d="M22.5837 13.4165L13.417 22.5832M13.417 13.4165L22.5837 22.5832"
                stroke="white"
                stroke-width="1.7"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </Box>
        }
      >
        <ContentCnt>
          {isClosing && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LoaderCnt>
                <CircularProgress />
              </LoaderCnt>
            </Box>
          )}
          <Box sx={{ p: '24px' }}>
            {/* Cover Image */}
            <CoverImageContainer onClick={handleViewFullscreen}>
              <CoverImage
                src={
                  announcement?.coverImage ||
                  S3_BUCKET_ASSETS + 'images/announcement-cover.png'
                }
                alt={announcement?.title}
              />
            </CoverImageContainer>

            {/* Announcement Title & Description */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                {announcement.title}
              </Typography>
              {announcement.pinned ? (
                <Box sx={pinnedContainer}>
                  <img
                    src={S3_BUCKET_ASSETS + 'images/pin-02.svg'}
                    alt="pinned"
                    style={{ marginBottom: '1px' }}
                  />
                  <Typography sx={pinnedText}>Pinned</Typography>
                </Box>
              ) : null}
            </Box>
            <Typography variant="body1" sx={{ mb: 1.5 }}>
              <Box
                sx={{
                  background: announcement?.color?.bgColor || '#FFFFFF',
                  padding: '12px',
                  borderRadius: '12px',
                  '& *': {
                    color: announcement?.color?.color || '#011B31',
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: announcement?.description || '',
                }}
              />
            </Typography>

            <Box
              sx={{
                height: '1px',
                width: '100%',
                backgroundColor: '#EEEEEE',
                marginBottom: 1.5,
              }}
            />

            {/* Attachments Section - Show if there are attachments */}
            {announcement.attachments && announcement.attachments.length > 0 && (
              <>
                <Typography
                  fontSize={14}
                  sx={{ mb: 1, fontWeight: 'bold' }}
                  color={'#212121'}
                >
                  Attachments
                </Typography>
                <AnnouncementAttachments
                  formData={announcement}
                  reviewMode={true}
                  compact={true}
                  iconSize={'medium'}
                  allowPreview={true}
                  listProps={{
                    sx: {
                      padding: '0',
                      border: 'none',
                      '& .attachementItem': {
                        boxShadow: '0px 8px 16px 0px #0000000F',
                        border: '1px solid #EEEEEE !important',
                        padding: '8px !important',
                      },
                      '& .itemCnt': {},
                      '& .itemName': {},
                    },
                  }}
                />
              </>
              // <AttachmentViewerSection attachments={announcement.attachments} />
            )}
            {(announcement.requirePhoto ||
              announcement.requireVideo ||
              announcement.requireSignature) && (
              <Requirments
                sx={{
                  ...(canAcknowledge()
                    ? {
                        background: '#E8F5E9',
                        borderColor: '#C8E6C9',
                        boxShadow: '0px 4px 8px 0px #49CF7129',
                      }
                    : {}),
                }}
              >
                <Typography
                  fontSize={'14px'}
                  fontWeight={700}
                  color={'#212121'}
                >
                  Requirements
                </Typography>
                <Typography
                  fontSize={'12px'}
                  mb={2}
                  fontWeight={600}
                  color={'#212121'}
                >
                  {!canAcknowledge()
                    ? 'Complete all required actions before acknowledging this announcement.'
                    : 'All required actions are completed.'}
                </Typography>

                {announcement.requirePhoto && (
                  <ImageUploaderSection
                    announcement={announcement}
                    photos={photos}
                    setPhotos={setPhotos}
                    readOnly={readOnly}
                  />
                )}
                {/* Video Section */}
                {announcement.requireVideo && (
                  <VideoUploaderSection
                    announcement={announcement}
                    video={video}
                    setVideo={setVideo}
                    readOnly={readOnly}
                  />
                )}

                {/* Signature Section */}
                {announcement.requireSignature && (
                  <SignatureSection
                    signature={signature}
                    setSignature={setSignature}
                    announcement={announcement}
                    readOnly={readOnly}
                  />
                )}
              </Requirments>
            )}

            {/* Error message */}
            {error && (
              <Typography variant="body2" color="error" sx={{ mt: 2, mb: 1 }}>
                {error}
              </Typography>
            )}

            {/* Action Buttons */}
            {announcement.requireAcknowledgement && !readOnly && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 2,
                  mt: 4,
                }}
              >
                <CustomButton
                  variant="contained"
                  onClick={() => handleAcknowledge(true)}
                  startIcon={<CheckIcon />}
                  disabled={loading || !canAcknowledge()}
                  sx={{ borderRadius: '6px', width: '100%' }}
                  buttonLoader={loading}
                >
                  I Acknowledge
                </CustomButton>
              </Box>
            )}
          </Box>
        </ContentCnt>
      </CustomDialog>
    </>
  );
};

export default AcknowledgeAnnouncementDialog;
